import React from "react";
import { Typography } from "@mui/material";
import {
    sleepTheme,
    sleepTableFrameStyle,
    sleepTableTheme,
} from "../../sleepReportTheme";

const titleStyle = {
    fontFamily: "Poppins",
    fontSize: "18px",
    fontWeight: 600,
    color: sleepTheme.text,
};

const titleUnderlineStyle = {
    width: "100%",
    height: "2px",
    backgroundColor: sleepTheme.stroke,
    marginBottom: "20px",
};

const disclaimerStyle = {
    fontFamily: "Poppins",
    fontSize: "12px",
    color: sleepTheme.textMuted,
    marginBottom: "38px",
    lineHeight: 1.4,
    display: "block",
    width: "100%",
};

const thresholdStyle = {
    fontFamily: "Poppins",
    fontSize: "15px",
    fontWeight: 700,
    color: sleepTheme.text,
    marginBottom: "12px",
};

const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: "Poppins",
    fontSize: "14px",
    backgroundColor: sleepTableTheme.frameBg,
};

const thStyle = {
    padding: "10px 14px",
    textAlign: "left",
    fontWeight: 700,
    color: sleepTheme.text,
    backgroundColor: sleepTableTheme.headerBg,
    borderBottom: `2px solid ${sleepTableTheme.headerBorder}`,
    borderRight: `1px solid ${sleepTableTheme.cellBorder}`,
};

const getTdStyle = (rowIndex) => ({
    padding: "10px 14px",
    borderBottom: `1px solid ${sleepTableTheme.cellBorder}`,
    borderRight: `1px solid ${sleepTableTheme.cellBorder}`,
    backgroundColor:
        rowIndex % 2 === 0
            ? sleepTableTheme.rowBg
            : sleepTableTheme.rowAltBg,
    color: sleepTheme.text,
    textAlign: "left",
});

const containerStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    alignSelf: "stretch",
    width: "100%",
};

const episodeCardStyle = {
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "52px",
    border: `1px solid ${sleepTheme.stroke}`,
    backgroundColor: sleepTheme.surface,
};

const DISCLAIMER_TEXT =
    "The environmental data is for informational use only. Environmental source data accuracy has not been validated.";

/**
 * Renders a list of drop episodes, each with a header (title, disclaimer, alert threshold) and a table of rows.
 */
export default function DropEpisodesSection({ episodes = [] }) {
    if (!episodes.length) return null;

    return (
        <div style={containerStyle}>
            {episodes.map((episode, index) => (
                <div key={index} style={episodeCardStyle}>
                    <div>
                        <Typography style={titleStyle}>
                            Drop Episode {index + 1}
                        </Typography>
                        <div style={titleUnderlineStyle} />
                        <div style={disclaimerStyle}>{DISCLAIMER_TEXT}</div>
                        <Typography style={thresholdStyle}>
                            Alert Threshold:{" "}
                            {episode.alert_threshold != null
                                ? `${episode.alert_threshold}%`
                                : "—"}
                        </Typography>
                    </div>
                    <div style={sleepTableFrameStyle}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Time</th>
                                    <th style={thStyle}>SpO₂</th>
                                    <th style={thStyle}>PR</th>
                                    <th style={thStyle}>Alt</th>
                                    <th style={thStyle}>Hum</th>
                                    <th style={thStyle}>AQI</th>
                                    <th style={thStyle}>Baro:Index</th>
                                    <th
                                        style={{
                                            ...thStyle,
                                            borderRight: "none",
                                        }}
                                    >
                                        Patient Notes
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {(episode.rows || []).map((row, rowIndex) => {
                                    const cellStyle = getTdStyle(rowIndex);
                                    const lastCellStyle = {
                                        ...cellStyle,
                                        borderRight: "none",
                                    };

                                    return (
                                        <tr key={rowIndex}>
                                            <td style={cellStyle}>
                                                {row.time ?? "—"}
                                            </td>
                                            <td style={cellStyle}>
                                                {row.spo2 ?? "—"}
                                            </td>
                                            <td style={cellStyle}>
                                                {row.hr ?? "—"}
                                            </td>
                                            <td style={cellStyle}>
                                                {row.alt ?? "—"}
                                            </td>
                                            <td style={cellStyle}>
                                                {row.hum ?? "—"}
                                            </td>
                                            <td style={cellStyle}>
                                                {row.aqi ?? "—"}
                                            </td>
                                            <td style={cellStyle}>
                                                {row.baroIndex ??
                                                    row.baro ??
                                                    "—"}
                                            </td>
                                            <td style={lastCellStyle}>
                                                {row.patientNotes ??
                                                    row.patient_notes ??
                                                    "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    );
}
